import { Box, Chip, Stack, Typography } from '@mui/material'
import { Icon, Image, type ImageData } from '@media-components'
import { BaselineStyleClasses, CustomStyles, getStyles } from '@utils/styles'
import { ActionButton, type ActionButtonData } from '../ActionButton'
import {
  SimpleDocumentRenderer,
  type SimpleDocumentRendererData,
} from '../DocumentRenderer'
import defaultStyles, { StylesClassNames } from './styles'

export interface CostimizerFeatureCardData {
  title: string
  description?: string
  points?: string[]
  imageUrl?: ImageData
  tagName?: string
  documentData?: SimpleDocumentRendererData
  actionButtonData?: ActionButtonData
  darkBg?: boolean
}

export type CostimizerFeatureCardProps = {
  isReverse?: boolean
  csx?: CustomStyles<StylesClassNames>
} & CostimizerFeatureCardData

export function CostimizerFeatureCard({
  title,
  description,
  points,
  imageUrl,
  isReverse = false,
  documentData,
  tagName,
  actionButtonData,
  csx,
  darkBg = false,
}: CostimizerFeatureCardProps) {
  const styles = getStyles<StylesClassNames>(defaultStyles, csx)

  return (
    <Stack
      direction={{ xs: 'column', md: isReverse ? 'row' : 'row-reverse' }}
      spacing={5}
      {...styles('wrapper')}
      alignItems="center"
      justifyContent="space-between"
      className={BaselineStyleClasses.imageTransitioningWrapper}
    >
      <Stack spacing={5} flex={1}>
        <Stack spacing={2}>
          {tagName && (
            <div>
              <Chip
                label={
                  <Typography variant="caption1.600">{tagName}</Typography>
                }
                variant="trans"
                color="primary"
                sx={{
                  color: darkBg ? 'highlight.dark' : 'primary.main',
                }}
              />
            </div>
          )}
          {title && (
            <Typography
              variant="h5.700"
              color={darkBg ? 'text.primaryDark' : 'text.primary'}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography
              variant="body1.500"
              color={darkBg ? 'text.primaryDark' : 'text.secondary'}
            >
              {description}
            </Typography>
          )}
          {documentData && (
            <SimpleDocumentRenderer
              document={documentData.document}
              showTickMarks
              sx={{
                marginTop: '0px !important',
                marginBottom: '0px !important',
              }}
              listSx={{
                marginTop: '60px !important',
              }}
              alternateBg={darkBg}
            />
          )}
          {actionButtonData && (
            <ActionButton
              {...actionButtonData}
              size="medium"
              variant="outlined"
              type="submit"
              darkTheme={darkBg}
              showArrow
              noAnimation
            />
          )}
        </Stack>
        {!!points?.length && (
          <Stack spacing={3}>
            {points.map((point, index) => (
              <Box key={index} {...styles('listItem')}>
                <Icon
                  iconName="TickCircle"
                  colorValues="green.700"
                  type="solid"
                  currentValue={false}
                />
                <Typography
                  variant="body3.500"
                  color={darkBg ? 'text.primaryDark' : 'text.primary'}
                >
                  {point}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
      {imageUrl && <Image sx={styles('image').sx} {...imageUrl} fullWidth />}
    </Stack>
  )
}
